/**
 * Single modal manager. Replaces the 4 competing legacy patterns
 * (openModal/closeModal, drawer.open class, aria-hidden direct toggle,
 * ModalZoomGuard hardcoded selectors).
 *
 * Conventions:
 *   - Modal overlay element ID is the registration key.
 *   - Overlay has `[aria-hidden]` flipped between "true"/"false".
 *   - Inner `.drawer` element (if present) gets `.open` class toggled.
 *   - `body.no-scroll` is added while any modal is open.
 */

import { appStore } from './store.js';

/**
 * @typedef {Object} ModalOptions
 * @property {() => void} [onOpen]
 * @property {() => void} [onClose]
 * @property {boolean} [closeOnBackdrop=true]
 * @property {boolean} [closeOnEscape=true]
 */

/** @type {Map<string, ModalOptions>} */
const registry = new Map();

/** @type {Set<string>} */
const openIds = new Set();

let backdropWired = false;
let escapeWired = false;

/**
 * Register modal options. Calling open() with an unregistered ID still works
 * with default behaviour — registration is only needed for hooks.
 * @param {string} id
 * @param {ModalOptions} [options]
 */
export function register(id, options = {}) {
  registry.set(id, { closeOnBackdrop: true, closeOnEscape: true, ...options });
}

/**
 * Open a modal by its overlay element ID.
 * @param {string} id
 * @returns {boolean} true if opened, false if element missing
 */
export function open(id) {
  const overlay = document.getElementById(id);
  if (!overlay) return false;

  overlay.style.display = 'flex';
  overlay.setAttribute('aria-hidden', 'false');
  const drawer = overlay.querySelector('.drawer');
  if (drawer) {
    requestAnimationFrame(() => drawer.classList.add('open'));
  }
  document.body.classList.add('no-scroll');

  openIds.add(id);
  appStore.set((s) => ({ ...s, openModals: new Set([...s.openModals, id]) }));

  wireBackdropOnce();
  wireEscapeOnce();

  registry.get(id)?.onOpen?.();
  return true;
}

/**
 * Close a modal.
 * @param {string} id
 */
export function close(id) {
  const overlay = document.getElementById(id);
  if (!overlay) {
    openIds.delete(id);
    return;
  }
  const drawer = overlay.querySelector('.drawer');
  if (drawer) drawer.classList.remove('open');
  overlay.setAttribute('aria-hidden', 'true');

  // Delay display:none to allow exit animation.
  setTimeout(() => {
    if (overlay.getAttribute('aria-hidden') === 'true') overlay.style.display = 'none';
  }, 300);

  openIds.delete(id);
  appStore.set((s) => {
    const next = new Set(s.openModals);
    next.delete(id);
    return { ...s, openModals: next };
  });
  if (openIds.size === 0) document.body.classList.remove('no-scroll');

  registry.get(id)?.onClose?.();
}

/** Close every open modal. */
export function closeAll() {
  [...openIds].forEach(close);
}

/** @returns {boolean} */
export function isOpen(/** @type {string} */ id) {
  return openIds.has(id);
}

/** @returns {string[]} */
export function activeIds() {
  return [...openIds];
}

function wireBackdropOnce() {
  if (backdropWired) return;
  backdropWired = true;
  document.addEventListener('click', (e) => {
    const target = /** @type {HTMLElement} */ (e.target);
    if (!target || !target.classList?.contains('drawer-overlay')) return;
    const id = target.id;
    if (!id || !openIds.has(id)) return;
    const opts = registry.get(id);
    if (opts && opts.closeOnBackdrop === false) return;
    close(id);
  });
}

function wireEscapeOnce() {
  if (escapeWired) return;
  escapeWired = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || openIds.size === 0) return;
    const topId = [...openIds].pop();
    if (!topId) return;
    const opts = registry.get(topId);
    if (opts && opts.closeOnEscape === false) return;
    close(topId);
  });
}
